import React from 'react';

import {
  getAllAdminItems,
  ADMIN_CONFIG,
  type AdminItem,
} from '@app/constants/admin-config';

type LazyRouteFunction = () => Promise<{ Component: React.ComponentType }>;
const lazyRouteCache = new Map<string, LazyRouteFunction>();

/**
 * Creates a lazy route function compatible with React Router v6 lazy routes
 */
const createLazyRoute = (componentPath: string) => {
  if (lazyRouteCache.has(componentPath)) {
    return lazyRouteCache.get(componentPath) as LazyRouteFunction;
  }

  const lazyRoute = async () => {
    if (!componentPath.startsWith('@app/components/Admin/')) {
      throw new Error(
        `Invalid component path format: ${componentPath}. Must start with @app/components/Admin/`,
      );
    }

    const adminPath = componentPath.replace('@app/components/Admin/', '');

    try {
      const module = await import(
        /* webpackChunkName: "admin-[request]" */
        /* webpackInclude: /\.tsx?$/ */
        /* webpackExclude: /__tests__|\.test\.tsx?$/ */
        `../Admin/${adminPath}`
      );
      return { Component: module.default };
    } catch (error) {
      try {
        const moduleWithExt = await import(
          /* webpackChunkName: "admin-[request]" */
          /* webpackInclude: /\.tsx?$/ */
          /* webpackExclude: /__tests__|\.test\.tsx?$/ */
          `../Admin/${adminPath}.tsx`
        );
        return { Component: moduleWithExt.default };
      } catch (extError) {
        throw new Error(
          `Failed to load component at path: ${componentPath}. Admin path: ${adminPath}. Errors: ${error}, ${extError}`,
        );
      }
    }
  };

  lazyRouteCache.set(componentPath, lazyRoute);
  return lazyRoute;
};

/**
 * Route configuration object for React Router v6
 */
interface RouteConfig {
  path: string;
  lazy: LazyRouteFunction;
  id?: string;
  loader?: (args?: {
    params: Record<string, string | undefined>;
  }) => Promise<unknown>;
}

/**
 * Recursively generates route configs from admin items
 */
const generateRouteConfigsFromItems = (items: AdminItem[]): RouteConfig[] => {
  const routes: RouteConfig[] = [];

  items.forEach((item) => {
    if (item.hasRoute && item.componentPath) {
      routes.push({
        path: item.url.startsWith('/') ? item.url.substring(1) : item.url, // Remove leading slash for React Router v6
        lazy: createLazyRoute(item.componentPath),
        id: `admin-${item.id}`,
      });
    }

    if (item.children && item.children.length > 0) {
      routes.push(...generateRouteConfigsFromItems(item.children));
    }
  });

  return routes;
};

/**
 * Generates all admin route configurations dynamically from the config
 */
export const generateAdminRouteConfigs = (): RouteConfig[] => {
  const allItems = getAllAdminItems();
  const routes: RouteConfig[] = [];

  allItems.forEach((item) => {
    if (item.hasRoute && item.componentPath) {
      const basePath = item.url.startsWith('/')
        ? item.url.substring(1)
        : item.url;

      routes.push({
        path: basePath,
        lazy: createLazyRoute(item.componentPath),
        id: `admin-${item.id}`,
        loader: item.loader,
      });

      if (item.url.startsWith('/admin/personal')) {
        const pathWithUserId = basePath.replace(
          /^admin\/personal/,
          'admin/personal/:userId',
        );

        routes.push({
          path: pathWithUserId,
          lazy: createLazyRoute(item.componentPath),
          id: `admin-${item.id}-with-user-id`,
        });
      }
    }
  });

  return routes;
};

/**
 * Generates route configs for a specific admin section
 */
export const generateSectionRouteConfigs = (
  sectionId: string,
): RouteConfig[] => {
  const section = ADMIN_CONFIG.find((s: { id: string }) => s.id === sectionId);
  if (!section) {
    return [];
  }
  return generateRouteConfigsFromItems(section.items);
};

/**
 * Helper to get all admin paths for route matching
 */
export const getAllAdminPaths = (): string[] => {
  const allItems = getAllAdminItems();
  return allItems.filter((item) => item.hasRoute).map((item) => item.url);
};

/**
 * Helper to check if a path is an admin route
 */
export const isAdminRoute = (path: string): boolean =>
  path.startsWith('/admin');

/**
 * Hook to get route info for a specific admin item
 */
export const useAdminRouteInfo = (itemId: string) => {
  const allItems = getAllAdminItems();
  const item = allItems.find((i) => i.id === itemId);

  return {
    item,
    hasRoute: item?.hasRoute || false,
    componentPath: item?.componentPath,
    url: item?.url,
  };
};

export type { RouteConfig };
